import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, PlayCircleIcon, UsersIcon } from 'lucide-react';
import { Container, Section, SectionHeader, Kicker, Reveal } from '../components/ui/Primitives';
import { Button } from '../components/ui/Button';
import { HeroArcs } from '../components/sections/HeroVisual';
import { Seo } from '../components/Seo';
import { SessionTime, sessionDateLabel } from '../components/webinars/SessionTime';
import { WEBINARS, SESSION_TIME, nextWebinar, pastWebinars, webinarStatus } from '../data/webinars';
import { breadcrumbSchema, canonical, SITE } from '../lib/seo';
export function Webinars() {
  const featured = nextWebinar();
  const replays = pastWebinars();
  const schema = [{
    '@type': 'EventSeries',
    '@id': `${canonical('/webinars')}#series`,
    name: 'Cavalla 2027 Webinar Programme',
    description: 'A monthly webinar series for government officials on national plan delivery, government data, responsible AI in the public service, and development finance. Every session is the third Thursday of the month at 14:00 GMT and runs for sixty minutes.',
    url: canonical('/webinars'),
    organizer: {
      '@id': `${SITE.url}/#organisation`
    },
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    inLanguage: 'en-GB'
  }, breadcrumbSchema([{
    name: 'Home',
    path: '/'
  }, {
    name: 'Webinars',
    path: '/webinars'
  }])];
  return <>
      <Seo title="Webinars for government: the 2027 programme" description="A monthly webinar for government officials on plan delivery, government data and responsible AI. Third Thursday of the month, 14:00 GMT, sixty minutes, free to attend." path="/webinars" jsonLd={schema} data-id="element-1660" data-mp="33r5mkxe6a6ds" />

      {/* Hero */}
      <section className="surface-dark relative isolate overflow-hidden" data-id="element-1661" data-mp="2kz35xm1j6q3j">
        <HeroArcs className="pointer-events-none absolute -right-12 top-0 h-[360px] w-[360px] opacity-80" data-id="element-1662" data-mp="2g00b0uf9m09a" />
        <Container className="relative py-14 sm:py-20" data-id="element-1663" data-mp="26bl9tc21b3sq">
          <Kicker data-id="element-1664" data-mp="26e7ft79a1b8u">The 2027 programme</Kicker>
          <h1 className="mt-4 max-w-4xl font-display text-[34px] font-light leading-tight text-white sm:text-[50px]" data-id="element-1665" data-mp="2bbqv8ahpnu2d">
            One hour a month with the people delivering national plans.
          </h1>
          <p className="mt-6 max-w-2xl text-[17px] leading-relaxed text-white/72 sm:text-[19px]" data-id="element-1666" data-mp="21c8bnv5pqaau">
            Twelve sessions for officials in ministries, agencies and revenue authorities, on the problems
            that decide whether a plan is delivered: seeing progress, reporting it credibly, turning
            government data into decisions, and putting AI to work safely.
          </p>
          <p className="mt-7 text-[15px] text-white/72" data-id="element-1667" data-mp="2kbdyz2xwxbyl">
            Every session is the third Thursday of the month at {SESSION_TIME.full}. Sixty minutes, free to
            attend, and recorded.
          </p>
          {featured && <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-1668" data-mp="3ctrngbxczll0">
              <Button to={`/webinars/${featured.slug}`} size="lg" shape="pill" data-id="element-1669" data-mp="11cg1ug4bwa7o">
                Register for the next session
              </Button>
              <Button href="#calendar" variant="reversed" size="lg" shape="pill" data-id="element-1670" data-mp="wc0k7cd0kgjw">
                See the full calendar
              </Button>
            </div>}
        </Container>
      </section>

      {/* Next session */}
      {featured && <Section aria-labelledby="next-session" data-id="element-1671" data-mp="ifepubccx9u8">
          <SectionHeader id="next-session" kicker="Next session" title={featured.title} data-id="element-1672" data-mp="ig6m7ods3blk" />
          <div className="mt-8 grid gap-10 lg:grid-cols-[1.15fr_0.85fr]" data-id="element-1673" data-mp="dhdc85xpa2q5">
            <div data-id="element-1674" data-mp="rglc711oteve">
              <p className="max-w-prose text-[17px] leading-relaxed text-body" data-id="element-1675" data-mp="rcxfjt0y4ccg">{featured.description}</p>
              <dl className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-1676" data-mp="nfj7i4p04am1">
                <div data-id="element-1677" data-mp="22svv8fqmpwmn">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1678" data-mp="22sdt52haco6t">
                    Who it is for
                  </dt>
                  <dd className="mt-2 text-[16px] text-ink" data-id="element-1679" data-mp="2u3jx42bh25sk">{featured.audience}</dd>
                </div>
                <div data-id="element-1680" data-mp="2y5evaxy3w324">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1681" data-mp="2y4vn48y9ct1a">Focus</dt>
                  <dd className="mt-2 text-[16px] text-ink" data-id="element-1682" data-mp="3325brmdtp0o2">{featured.focus}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-md border border-line bg-mist p-7" data-id="element-1683" data-mp="3d1w4pjmqj7m1">
              <SessionTime webinar={featured} data-id="element-1684" data-mp="3czjnx9spr3ak" />
              <div className="mt-6 border-t border-line pt-6" data-id="element-1685" data-mp="3h2g28ob80d8p">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted" data-id="element-1686" data-mp="3lty37cd8udto">Speakers</p>
                <ul className="mt-3 space-y-2" data-id="element-1687" data-mp="3qsrjfcjx0ixj">
                  {featured.speakers.map(speaker => <li key={speaker.name} className="flex items-start gap-2.5 text-[15px] text-body" data-id="element-1688" data-mp="3vsdsrawmhf12">
                      <UsersIcon className="mt-1 h-4 w-4 shrink-0 text-gold" aria-hidden="true" data-id="element-1689" data-mp="4lb6mxvn5yxp" />
                      <span data-id="element-1690" data-mp="1p4em0xigyw3a">
                        <span className="text-ink" data-id="element-1691" data-mp="1p4mxqm2uvvcw">{speaker.name}</span>, {speaker.role}
                      </span>
                    </li>)}
                </ul>
              </div>
              <Button to={`/webinars/${featured.slug}`} size="lg" className="mt-7 w-full justify-center" data-id="element-1692" data-mp="1f6h04go31bdu">
                Register for this session
              </Button>
            </div>
          </div>
        </Section>}

      {/* Calendar */}
      <Section id="calendar" tone="mist" aria-labelledby="calendar-heading" data-id="element-1693" data-mp="22v9w2d4pfj8g">
        <SectionHeader id="calendar-heading" kicker="The full calendar" title="Twelve sessions, one a month." lead="Each session stands alone, so you can take the ones that matter to your unit. Registrants receive the recording and a one page summary whether or not they attend." data-id="element-1694" data-mp="22xcxdtbb4ysh" />

        <ol className="mt-12 divide-y divide-line border-y border-line" data-id="element-1695" data-mp="2lue4bjof98z2">
          {WEBINARS.map(webinar => {
          const past = webinarStatus(webinar) === 'past';
          return <li key={webinar.slug} data-id="element-1696" data-mp="ovf6piihj1t7">
                <Link to={`/webinars/${webinar.slug}`} className="group grid gap-4 py-7 transition-colors duration-200 hover:bg-panel/60 lg:grid-cols-[150px_1fr_auto] lg:items-center lg:gap-8" data-id="element-1697" data-mp="oudlil7r8yxr">
                  <div data-id="element-1698" data-mp="1644dwlhkzcy">
                    <p className="font-display text-[19px] leading-none text-gold" data-id="element-1699" data-mp="15unqzmc04jx">{webinar.month}</p>
                    <p className="mt-2 text-small text-muted" data-id="element-1700" data-mp="57y0rsonfh7b">{sessionDateLabel(webinar)}</p>
                  </div>

                  <div data-id="element-1701" data-mp="3nem4pf7ypybe">
                    <h3 className="font-display text-[20px] leading-snug text-ink sm:text-[23px]" data-id="element-1702" data-mp="3nf5ajxkc8w92">
                      {webinar.title}
                    </h3>
                    <p className="mt-2 text-[15px] text-body" data-id="element-1703" data-mp="1lmza73ov3la6">
                      {webinar.focus} · {webinar.audience}
                    </p>
                  </div>

                  <span className="inline-flex items-center gap-2 text-[15px] font-medium text-royal" data-id="element-1704" data-mp="3b2tiir8lf8il">
                    {past ? 'Watch the replay' : 'Register'}
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" data-id="element-1705" data-mp="365taias6nvou" />
                  </span>
                </Link>
              </li>;
        })}
        </ol>
      </Section>

      {/* Replays */}
      <Section aria-labelledby="replays" data-id="element-1706" data-mp="3ouq40cfrak60">
        <Reveal data-id="element-1707" data-mp="3otyad75pywid">
          <Link to="/webinars/replays" className="group flex flex-col gap-6 rounded-md border border-line bg-panel p-8 transition-colors duration-200 hover:border-gold sm:flex-row sm:items-center sm:justify-between" data-id="element-1708" data-mp="3owkgr3gvj5z0">
            <div className="max-w-2xl" data-id="element-1709" data-mp="29cxv22i7iy5o">
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-gold" data-id="element-1710" data-mp="29dzh120rh526">
                Replay library
              </p>
              <h2 id="replays" className="mt-4 font-display text-[27px] leading-tight text-ink" data-id="element-1711" data-mp="1ilz1cq325w1q">
                Missed a session
              </h2>
              <p className="mt-3 text-[16px] text-body" data-id="element-1712" data-mp="1nipg9a34mllk">
                {replays.length > 0 ? `${replays.length} recorded ${replays.length === 1 ? 'session' : 'sessions'}, each with its one page summary.` : 'Recordings appear here after each session, each with its one page summary.'}
              </p>
            </div>
            <span className="inline-flex shrink-0 items-center gap-2 text-[15px] font-medium text-royal" data-id="element-1713" data-mp="14l6786go52z5">
              <PlayCircleIcon className="h-5 w-5" aria-hidden="true" data-id="element-1714" data-mp="19kizx7u8149n" />
              Open the library
            </span>
          </Link>
        </Reveal>
      </Section>
    </>;
}